/**
 * Search Ticker - Animated placeholder text for search input
 * Creates a typewriter effect cycling through search suggestions
 */

class SearchTicker extends HTMLElement {
  constructor() {
    super();
    this.phrases = [];
    this.currentPhraseIndex = 0;
    this.currentCharIndex = 0;
    this.isDeleting = false;
    this.isPaused = false;
    this.typeSpeed = 80;
    this.deleteSpeed = 40;
    this.pauseDuration = 2000;
    this.textElement = null;
    this.cursorElement = null;
    this.input = null;
    this.animationFrame = null;
  }

  connectedCallback() {
    // Get phrases from data attribute
    const phrasesAttr = this.getAttribute('data-phrases');
    if (phrasesAttr) {
      try {
        this.phrases = JSON.parse(phrasesAttr);
      } catch (e) {
        this.phrases = phrasesAttr.split(',').map(p => p.trim());
      }
    }

    if (this.phrases.length === 0) {
      this.phrases = ['products', 'brands', 'categories', 'deals'];
    }

    // Find elements
    this.textElement = this.querySelector('.search-ticker__text');
    this.cursorElement = this.querySelector('.search-ticker__cursor');

    // Find associated input
    const inputId = this.getAttribute('data-input');
    if (inputId) {
      this.input = document.getElementById(inputId);
    } else {
      this.input = this.closest('.field')?.querySelector('input');
    }

    // Start animation
    this.startAnimation();

    // Handle input focus/blur
    if (this.input) {
      this.input.addEventListener('focus', () => this.pause());
      this.input.addEventListener('blur', () => {
        if (!this.input.value) {
          this.resume();
        }
      });
      this.input.addEventListener('input', () => {
        if (this.input.value) {
          this.pause();
        } else {
          this.resume();
        }
      });
    }
  }

  disconnectedCallback() {
    this.stopAnimation();
  }

  startAnimation() {
    this.animate();
  }

  stopAnimation() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
  }

  pause() {
    this.isPaused = true;
    this.style.opacity = '0';
    this.style.visibility = 'hidden';
  }

  resume() {
    this.isPaused = false;
    this.style.opacity = '';
    this.style.visibility = '';
    this.animate();
  }

  animate() {
    if (this.isPaused) return;

    const currentPhrase = this.phrases[this.currentPhraseIndex];

    if (!this.isDeleting) {
      // Typing
      if (this.currentCharIndex < currentPhrase.length) {
        if (this.textElement) {
          this.textElement.textContent = currentPhrase.substring(0, this.currentCharIndex + 1);
        }
        this.currentCharIndex++;
        setTimeout(() => this.animate(), this.typeSpeed);
      } else {
        // Finished typing, pause then delete
        setTimeout(() => {
          this.isDeleting = true;
          this.animate();
        }, this.pauseDuration);
      }
    } else {
      // Deleting
      if (this.currentCharIndex > 0) {
        if (this.textElement) {
          this.textElement.textContent = currentPhrase.substring(0, this.currentCharIndex - 1);
        }
        this.currentCharIndex--;
        setTimeout(() => this.animate(), this.deleteSpeed);
      } else {
        // Finished deleting, move to next phrase
        this.isDeleting = false;
        this.currentPhraseIndex = (this.currentPhraseIndex + 1) % this.phrases.length;
        setTimeout(() => this.animate(), 300);
      }
    }
  }
}

// Register custom element
if (!customElements.get('search-ticker')) {
  customElements.define('search-ticker', SearchTicker);
}
